// organize-imports-ignore
// console.debug(`${new Date().toISOString()} ${performance.now().toFixed(2)}ms [${import.meta.url}]`)

// 1️⃣ Core Vue & Third-party Libraries
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { installRipple } from './ui/ripple'

// 2️⃣ App Core Modules
import App from './App.vue'
import router from './router'

// 3️⃣ Global Styles
import './assets/style-base/layers.css' // first, before everything else
import './assets/style-base/colorDefs.css'
import './assets/style-base/tokens.css'
import './assets/style-base/base-layer.scss'
import './assets/style-ui/ui-layer.scss'
import './assets/style-ui/utilities.scss'

import { loadEcharts } from './utils/echartsLoader'
import { loadKatex } from './utils/katexLoader'

// --- Types & Serialization ---
interface SerializedError {
    name?: string
    message: string
    stack?: string
}

function serializeError(err: unknown): SerializedError {
    if (err instanceof Error) {
        return {
            name: err.name,
            message: err.message,
            stack: err.stack
        }
    }
    return { message: typeof err === 'string' ? err : String(err) }
}

// --- Dynamic Route to Global Error ---
let isRedirectingToError = false

const routeToGlobalError = (label: string, error: unknown) => {
    console.error(`[${label}]`, error)

    // Evitar bucles infinitos de redirección si falla la pantalla de error
    if (isRedirectingToError || router.currentRoute.value.name === 'globalError') {
        return
    }
    isRedirectingToError = true

    const serialized = serializeError(error)

    // Recomendación: Almacenar en sessionStorage en lugar de la URL para no saturarla ni exponer datos
    try {
        sessionStorage.setItem('last_global_error', JSON.stringify({ label, ...serialized }))
    } catch {
        /* Fallback silencioso por si sessionStorage no está disponible */
    }

    router.push({ name: 'globalError' }).finally(() => {
        isRedirectingToError = false
    })
}

// --- Noise Filters (Ajustados para no filtrar errores reales) ---
const isNoiseError = (reason: unknown): boolean => {
    if (reason == null) return true

    const message = reason instanceof Error ? reason.message : String(reason)

    // Ignorar errores típicos del navegador que no afectan al usuario
    const noisePatterns = [
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        'Skipped ViewTransition',
        'Script error.'
    ]

    return noisePatterns.some((pattern) => message.includes(pattern))
}

// --- Global Listener Setup ---
window.onerror = function (message, url, line, col, error) {
    const errPayload = error ?? message
    if (!isNoiseError(errPayload)) {
        routeToGlobalError('Global JS error', errPayload)
    }
}

window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isNoiseError(event.reason)) {
        event.preventDefault()
        return
    }
    routeToGlobalError('Unhandled promise rejection', event.reason)
})

router.onError((err: unknown) => {
    routeToGlobalError('Navigation error', err)
})

// --- App Initialization ---
const app = createApp(App)
const pinia = createPinia()

app.config.errorHandler = (err, instance, info) => {
    // 'info' contiene la traza del componente Vue donde ocurrió el fallo
    routeToGlobalError(`Vue Error (${info})`, err)
}

// Plugins
app.use(pinia)
app.use(router)

installRipple()

app.mount('#app')

// warm load echars & katex when idle
const idle = window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1500))
idle(() => {
    loadEcharts()
    loadKatex()
})
